import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import { PageHeader } from "../components/ui";
import { api } from "../lib/api";

// M3X.COMP F.5 + F.6 — comp change detail page with state-machine
// next-action button + Article 60 consent panel.
export function CompChangeDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [consentDate, setConsentDate] = useState("");
  const [consentPdfUrl, setConsentPdfUrl] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const changeQ = useQuery({
    queryKey: ["comp-change", id],
    queryFn: () => api.getCompChange(id!),
    enabled: !!id,
  });
  const historyQ = useQuery({
    queryKey: ["comp-change-history", id],
    queryFn: () => api.compChangeHistory(id!),
    enabled: !!id,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["comp-change", id] });
    queryClient.invalidateQueries({ queryKey: ["comp-change-history", id] });
  };

  const trigger = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
      refresh();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const uploadConsent = useMutation({
    mutationFn: () =>
      api.compChangeUploadConsent(id!, {
        consent_date: consentDate,
        consent_pdf_url: consentPdfUrl,
      }),
    onSuccess: () => {
      setConsentDate("");
      setConsentPdfUrl("");
      refresh();
    },
    onError: (e: Error) => setError(e.message),
  });

  if (changeQ.isPending) return <Loading />;
  if (changeQ.isError)
    return <ErrorPanel message={(changeQ.error as Error).message} />;

  const c = changeQ.data!;
  const history = historyQ.data ?? [];

  return (
    <div className="space-y-4">
      <PageHeader
        title={
          c.change_number !== null
            ? `CC-${String(c.change_number).padStart(6, "0")}`
            : t("comp_change_detail.title")
        }
        subtitle={`${c.change_type} → ${c.workflow_state}`}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded border border-slate-200 bg-white p-4 text-sm">
          <div className="text-xs font-medium text-slate-700">
            {t("comp_change_detail.amounts")}
          </div>
          <dl className="mt-2 space-y-1 text-xs">
            <div className="flex justify-between">
              <dt className="text-slate-500">{t("comp_change_detail.previous")}</dt>
              <dd>{c.previous_basic_salary ?? "—"}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">{t("comp_change_detail.new")}</dt>
              <dd>{c.new_basic_salary ?? "—"}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">{t("comp_change_detail.delta_pct")}</dt>
              <dd>{c.delta_pct ?? "—"}%</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">{t("comp_change_detail.effective")}</dt>
              <dd>{c.effective_date}</dd>
            </div>
          </dl>
          {c.change_reason && (
            <div className="mt-3 text-xs">
              <div className="text-slate-500">{t("comp_change_detail.reason")}</div>
              <div>{c.change_reason}</div>
            </div>
          )}
        </div>

        {c.article_60_required && (
          <div className="rounded border border-amber-300 bg-amber-50 p-4 text-sm">
            <div className="text-xs font-medium text-amber-900">
              {t("comp_change_detail.article_60_title")}
            </div>
            {c.article_60_consent_signed ? (
              <div className="mt-2 text-xs text-emerald-700">
                ✓ {t("comp_change_detail.consent_signed_on")}{" "}
                {c.article_60_consent_date}
              </div>
            ) : (
              <>
                <div className="mt-2 text-xs text-amber-800">
                  {t("comp_change_detail.consent_required_body")}
                </div>
                <div className="mt-3 space-y-2">
                  <button
                    type="button"
                    onClick={() =>
                      trigger(() =>
                        api.compChangeRequestConsent(id!, "en")
                      )
                    }
                    className="rounded border border-amber-400 bg-white px-2 py-1 text-xs hover:bg-amber-100"
                  >
                    {t("comp_change_detail.request_consent")}
                  </button>
                  <div className="space-y-1">
                    <input
                      type="date"
                      value={consentDate}
                      onChange={(e) => setConsentDate(e.target.value)}
                      className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                      placeholder={t("comp_change_detail.consent_date")}
                    />
                    <input
                      type="url"
                      value={consentPdfUrl}
                      onChange={(e) => setConsentPdfUrl(e.target.value)}
                      className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                      placeholder={t("comp_change_detail.pdf_url")}
                    />
                    <button
                      type="button"
                      disabled={
                        !consentDate ||
                        !consentPdfUrl ||
                        uploadConsent.isPending
                      }
                      onClick={() => uploadConsent.mutate()}
                      className="rounded bg-brand-deep px-2 py-1 text-xs text-white hover:bg-slate-900 disabled:opacity-50"
                    >
                      {t("comp_change_detail.upload_consent")}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* State-machine next-action button */}
      <div className="flex flex-wrap items-center gap-2">
        {c.workflow_state === "draft" && (
          <button
            type="button"
            onClick={() => trigger(() => api.compChangeSubmit(id!))}
            className="rounded bg-brand-deep px-3 py-1.5 text-xs text-white hover:bg-slate-900"
          >
            {t("comp_change_detail.submit")}
          </button>
        )}
        {c.workflow_state === "submitted" && (
          <button
            type="button"
            onClick={() =>
              trigger(() => api.compChangeRouteForApproval(id!))
            }
            className="rounded bg-brand-deep px-3 py-1.5 text-xs text-white hover:bg-slate-900"
          >
            {t("comp_change_detail.route_for_approval")}
          </button>
        )}
        {c.workflow_state === "pending_approval" && (
          <button
            type="button"
            onClick={() => trigger(() => api.compChangeApprove(id!))}
            className="rounded bg-emerald-700 px-3 py-1.5 text-xs text-white hover:bg-emerald-700"
          >
            {t("comp_change_detail.approve")}
          </button>
        )}
        {c.workflow_state === "approved" && (
          <button
            type="button"
            onClick={() => trigger(() => api.compChangeActivate(id!))}
            className="rounded bg-emerald-700 px-3 py-1.5 text-xs text-white hover:bg-emerald-700"
          >
            {t("comp_change_detail.activate")}
          </button>
        )}
        {c.workflow_state === "effective" && (
          <button
            type="button"
            onClick={() => trigger(() => api.compChangeClose(id!))}
            className="rounded bg-slate-700 px-3 py-1.5 text-xs text-white hover:bg-slate-900"
          >
            {t("comp_change_detail.close")}
          </button>
        )}
        {(c.workflow_state === "submitted" ||
          c.workflow_state === "pending_approval") && (
          <>
            <input
              type="text"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder={t("comp_change_detail.reject_reason")}
              className="rounded border border-slate-300 px-2 py-1 text-xs"
            />
            <button
              type="button"
              disabled={!rejectReason}
              onClick={() =>
                trigger(() => api.compChangeReject(id!, rejectReason))
              }
              className="rounded border border-rose-400 bg-white px-3 py-1.5 text-xs text-rose-700 hover:bg-rose-50 disabled:opacity-50"
            >
              {t("comp_change_detail.reject")}
            </button>
          </>
        )}
        {(c.workflow_state === "draft" ||
          c.workflow_state === "submitted" ||
          c.workflow_state === "pending_approval") && (
          <button
            type="button"
            onClick={() => trigger(() => api.compChangeCancel(id!))}
            className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
          >
            {t("comp_change_detail.cancel")}
          </button>
        )}
      </div>

      {error && (
        <div className="rounded border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          {error}
        </div>
      )}

      {/* Workflow event timeline */}
      <div>
        <div className="text-xs font-medium text-slate-700">
          {t("comp_change_detail.history")}
        </div>
        <ul className="mt-2 space-y-1">
          {history.map((h) => (
            <li
              key={h.id}
              className="rounded border border-slate-100 bg-white px-3 py-2 text-xs"
            >
              <span className="font-mono text-slate-500">
                {new Date(h.occurred_at).toISOString().replace("T", " ").slice(0, 19)}
              </span>
              {" — "}
              <span className="font-medium">{h.transition}</span>
              {" "}
              <span className="text-slate-500">
                ({h.from_state ?? "—"} → {h.to_state})
              </span>
              {h.reason && <div className="text-slate-600">{h.reason}</div>}
            </li>
          ))}
        </ul>
      </div>

      <Link
        to="/operations/transactions/comp"
        className="text-xs text-slate-600 hover:underline"
      >
        ← {t("common.back")}
      </Link>
    </div>
  );
}
